import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { T } from 'src/shared/helpers';
import { ZTooltip } from '../../../../../widgets/ztooltip';
import { DropdownActions } from '../../../../../widgets/dropdown';
import { ChevronIcon } from '../../../../../widgets/icons/scene';
import CheckIcon from 'src/shared/material_icons/navigation/check.svg';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import { ComposerRowActionButton } from './composerWorkspace';

// LA CARD DI UNA LISTA DELL'INSPECTOR (prototipo V3, .blk; utente, 2026-10-20): una regola, una condizione, un
// prezzo. Niente bordo: il colore delle card, e aperta il contenuto su un fondo incassato quattro pixel dentro.
// In testa il segno del tipo — o la parola che la lega alle altre, «If», «And if» — che al passaggio, o a card
// scelta, diventa la casella della selezione. Il clic sulla testata apre e chiude soltanto: scegliere è la casella,
// o ⇧ / ⌘ sulla testata. La maniglia è una linguetta a cavallo del bordo alto, al passaggio.

const Block = styled.div<{ $selected: boolean; $off: boolean }>`
    position: relative;
    min-width: 0;
    border-radius: 10px;
    background: var(--editor-panel, #ffffff);
    transition: box-shadow 130ms ease;

    ${(props) =>
        props.$off &&
        css`
            background: none;
            box-shadow: inset 0 0 0 1px var(--editor-sunken, #e9ebed);
        `}

    ${(props) =>
        props.$selected &&
        css`
            box-shadow: inset 0 0 0 1px var(--editor-accent-line, #e2b597);
        `}
`;

const Head = styled.div`
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 46px;
    align-items: center;
    gap: 2px;
    padding: 6px 6px 6px 4px;
    border-radius: 10px;
    color: var(--editor-fg, #262a30);
    cursor: pointer;
    user-select: none;
    outline: none;

    /* I comandi della card compaiono al passaggio, tranne quelli che dicono uno stato. */
    [data-block-commands] > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    ${Block}:hover & [data-block-commands] > *,
    &:focus-within [data-block-commands] > * {
        opacity: 1;
    }
`;

const Grip = styled.button`
    position: absolute;
    top: -6px;
    left: 50%;
    z-index: 1;
    display: flex;
    width: 40px;
    height: 12px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: var(--editor-panel, #ffffff);
    box-shadow: 0 0 0 1px var(--editor-sunken, #e9ebed);
    color: var(--editor-faint, #676a6e);
    cursor: grab;
    opacity: 0;
    transform: translateX(-50%);
    transition: opacity 110ms ease;

    ${Block}:hover &,
    &:focus-visible {
        opacity: 1;
    }

    svg {
        width: 14px;
        height: 14px;
        transform: rotate(90deg);
        fill: currentColor;
    }
`;

const Pick = styled.span<{ $word: boolean }>`
    position: relative;
    display: grid;
    flex: none;
    width: ${(props) => (props.$word ? '44px' : '24px')};
    height: 24px;
    margin-right: 2px;
    place-items: ${(props) => (props.$word ? 'center start' : 'center')};
    color: var(--editor-muted, #55595f);

    > svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const Word = styled.span`
    padding-left: 2px;
    color: var(--editor-muted, #55595f);
    font-size: 11px;
    white-space: nowrap;
`;

const Check = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    display: grid;
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    place-items: center;
    background: none;
    box-shadow: inset 0 0 0 1.5px var(--editor-faint, #676a6e);
    color: transparent;
    cursor: pointer;
    opacity: 0;
    transform: translate(-50%, -50%);

    /* Col fuoco da tastiera la casella si vede, come al passaggio (utente, 2026-10-22, accessibilità). */
    &:focus-visible {
        opacity: 1;
    }

    /* La casella è di 16, il bersaglio di 24 (WCAG 2.5.8). */
    &::after {
        content: '';
        position: absolute;
        inset: -4px;
    }

    &:hover {
        box-shadow: inset 0 0 0 1.5px var(--editor-muted, #55595f);
    }

    /* Scelta nell'arancio tenue (utente, 2026-10-20), come il pallino del default: lavato, filo e segno arancio testo. */
    &[aria-checked='true'] {
        background: var(--editor-accent-wash, #f6efe9);
        box-shadow: inset 0 0 0 1.5px var(--editor-accent, #a94700);
        color: var(--editor-accent, #a94700);
    }

    svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
    }
`;

/* Al passaggio, o a card scelta, il segno lascia il posto alla casella. */
const pickSwap = css`
    ${Pick} > :not(${Check}) {
        opacity: 0;
    }

    ${Pick} > ${Check} {
        opacity: 1;
    }
`;

// Solo una card che si sceglie ha la casella: le altre tengono il loro segno anche al passaggio.
const BlockHover = styled.div<{ $selected: boolean; $selectable: boolean }>`
    display: contents;

    ${(props) =>
        props.$selectable &&
        css`
            ${Block}:hover & {
                ${pickSwap}
            }
        `}

    ${(props) => props.$selectable && props.$selected && pickSwap}
`;

const Title = styled.div`
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
`;

const Commands = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 1px;
`;

// LA FRECCIA È IL BOTTONE CHE APRE LA CARD (utente, 2026-10-22, accessibilità). Prima era tutta la testata a fare da
// bottone, con dentro altri bottoni (la maniglia, la casella, il tipo, i comandi, il «…»): per uno screen reader i
// figli di un bottone non esistono. Ora la testata resta cliccabile col mouse, e da tastiera si apre dalla freccia,
// che dice il titolo della card e se è aperta.
const Chevron = styled.button<{ $open: boolean }>`
    display: inline-flex;
    width: 24px;
    height: 24px;
    flex: none;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: none;
    color: var(--editor-muted, #55595f);
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
        transform: rotate(${(props) => (props.$open ? '0deg' : '-90deg')});
        transition: transform 130ms ease;
    }
`;

const Body = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
    margin: 0 4px 4px;
    padding: 6px;
    border-radius: 6px;
    background: var(--editor-sunken, #e9ebed);
`;

/**
 * Il titolo di una card: che cosa fa, e sotto (tenue) su che cosa. ARANCIO SOLO SULLA CARD APERTA (utente, 2026-10-20):
 * le chiuse restano scure, così nell'elenco si vede quale si sta modificando.
 */
export const ComposerBlockTitle = styled.span<{ $off?: boolean; $open?: boolean }>`
    max-width: 100%;
    overflow: hidden;
    padding-left: 4px;
    color: ${(props) => (props.$off ? 'var(--editor-faint, #676a6e)' : props.$open ? 'var(--editor-accent, #a94700)' : 'var(--editor-fg, #262a30)')};
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ComposerBlockSubtitle = styled.span`
    max-width: 100%;
    overflow: hidden;
    padding-left: 4px;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** La lista delle card: un po' d'aria in cima, perché la linguetta della prima non resti tagliata. */
export const ComposerBlockList = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 10px;
    padding-top: 6px;
`;

export type ComposerBlockSelectMode = 'toggle' | 'range';

export const ComposerBlock: FC<{
    id: string;
    open: boolean;
    onToggleOpen: () => void;
    selected?: boolean;
    /** La casella, o ⇧ / ⌘ sulla testata: scegliere senza aprire. */
    onSelect?: (mode: ComposerBlockSelectMode) => void;
    /** Spenta: senza fondo, col filo, il titolo tenue. */
    off?: boolean;
    /** Il segno del tipo in testa. */
    glyph?: ReactNode;
    /** Oppure la parola che lega la card alle altre: «If», «And if», «Or if». */
    word?: string;
    title: ReactNode;
    /** I comandi al passaggio: Play, l'occhio. */
    commands?: ReactNode;
    /** Il «…» della card, a gruppi divisi da un filo. */
    menu?: ReactNode[][];
    onGripPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    children?: ReactNode;
}> = ({ id, open, onToggleOpen, selected = false, onSelect, off = false, glyph, word, title, commands, menu, onGripPointerDown, onKeyDown, children }) => (
    <Block $selected={selected} $off={off} data-reorder-id={id} data-reorder-level={0}>
        <BlockHover $selected={selected} $selectable={!!onSelect}>
            <Head
                onClick={(event) => {
                    if (onSelect && event.shiftKey) return onSelect('range');
                    if (onSelect && (event.metaKey || event.ctrlKey)) return onSelect('toggle');
                    onToggleOpen();
                }}
                onKeyDown={onKeyDown}
            >
                {onGripPointerDown && (
                    <ZTooltip title={T._('Drag to reorder · Alt+Arrow to move', 'Admin')}>
                        <Grip
                            type='button'
                            aria-label={T._('Drag to reorder', 'Admin')}
                            onClick={(event) => event.stopPropagation()}
                            onPointerDown={(event) => {
                                event.stopPropagation();
                                onGripPointerDown(event);
                            }}
                        >
                            <DragIcon />
                        </Grip>
                    </ZTooltip>
                )}
                <Pick $word={!!word}>
                    {word ? <Word>{word}</Word> : glyph}
                    {onSelect && (
                        <Check
                            type='button'
                            role='checkbox'
                            aria-checked={selected}
                            aria-label={selected ? T._('Deselect', 'Admin') : T._('Select', 'Admin')}
                            onClick={(event) => {
                                event.stopPropagation();
                                onSelect(event.shiftKey ? 'range' : 'toggle');
                            }}
                        >
                            <CheckIcon />
                        </Check>
                    )}
                </Pick>
                <Title id={`${id}-title`}>{title}</Title>
                <Commands data-block-commands onClick={(event) => event.stopPropagation()}>
                    {commands}
                    {menu && (
                        <DropdownActions
                            header={
                                <ComposerRowActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                                    <MoreIcon />
                                </ComposerRowActionButton>
                            }
                            sections={menu}
                            minWidth='236px'
                            orientation='RIGHT'
                        />
                    )}
                </Commands>
                <Chevron
                    type='button'
                    $open={open}
                    aria-expanded={open}
                    aria-labelledby={`${id}-title`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggleOpen();
                    }}
                >
                    <ChevronIcon />
                </Chevron>
            </Head>
        </BlockHover>
        {open && children && <Body>{children}</Body>}
    </Block>
);

/**
 * «+ NEW ACTION / NEW PRICE / NEW CONDITION» (prototipo, .btn.block; utente 2026-10-20): in fondo a una lista di card, a
 * tutta larghezza, bianco col filo grigio come l'Add delle tab.
 */
export const ComposerNewButton = styled.button`
    display: flex;
    width: 100%;
    height: 28px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-radius: 8px;
    background: var(--editor-panel, #ffffff);
    color: var(--editor-fg, #262a30);
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--editor-sunken, #e9ebed);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    svg {
        width: 13px;
        height: 13px;
        fill: currentColor;
    }
`;
